import InfluencerCrieteria from 'Components/campaigns/InfluencerCriteria';
import Loader from 'Components/Common/Loader';
import Stepper from 'Components/Common/Stepper'
import { PRIVILEGE } from 'constants/privilege';
import { useFormik } from 'formik';
import withAccessControl from 'hoc/withAccessControl';
import { usePrivileges } from 'hooks/useAuth';
import useDocumentTitle from 'hooks/useDocumentTitle';
import Error404 from 'pages/Error/Error404';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Card, CardBody, Form, Spinner } from 'reactstrap';
import { updateCampaignRequirement } from 'services/api/campaign';
import { fetchCampaignCreatives } from 'slices/campaigns/creatives/thunk';
import { markCampaignDataExpired } from 'slices/campaigns/reducer';
import { fetchCampaignRequirement } from 'slices/campaigns/requirements/thunk';
import { AppDispatch, RootState } from 'store';
import { getErrorMessage } from 'utils/helpers';
import * as Yup from "yup";

const steps = [
    { label: "", completed: true },
    { label: "Influencer Criteria", active: true },
    { label: "", active: false },
    { label: "", active: false }
];
const CampaignRequirement = () => {

    useDocumentTitle("Influencer Criteria - Campaign");

    const { campaignId } = useParams();

    const privileges = usePrivileges();
    
    const campaignRequirement = useSelector((state: RootState) => state.campaignRequirements.items[campaignId!])

    const requirementLoading = useSelector((state: RootState) => state.campaignRequirements.loading);

    const selectedAgency = useSelector((state: RootState) => state.auth.selectedAgency);

    const dispatch: AppDispatch = useDispatch();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (campaignId)
            dispatch(fetchCampaignRequirement(campaignId))
    }, [dispatch, campaignId])

    const formFields = useFormik({
        // enableReinitialize : use this flag when initial values needs to be changed
        enableReinitialize: true,

        initialValues: campaignRequirement?.campaign_requirement?.requirement?.details,
        validationSchema: Yup.object({
            gender: Yup.string().required("Please select influencer's gender"),
            niches: Yup.array().required().min(1, "Please select atleast one niche").max(3, "Maximum 3 niches can be selected"),
            follower_count: Yup.object({
                min: Yup.number().required("Please enter minimum number of Instagram followers").min(1, "Please enter minimum number of Instagram followers")
            }),
            age: Yup.object({
                min: Yup.number().nullable(),
                max: Yup.number().nullable(),
              }).test(
                'min-max-required-together',
                'Please set minimum and maximum age range.',
                function (value) {
                  const { min, max } = value || {};
                  if ((min && !max) || (max && !min)) {
                    return false;
                  }
                  return true;
                }
            ),
        }),
        onSubmit: async (campaignData) => {
            if (!campaignId) return;
            try {
                if(!privileges.includes(PRIVILEGE.CAMPAIGN.CAMPAIGN.EDIT))
                    throw new Error("You are not authorized to perform this action!");

                setLoading(true);
                await updateCampaignRequirement(selectedAgency?.uuid!, campaignRequirement.campaign_requirement.campaign.uuid!, campaignData);
                await dispatch(fetchCampaignCreatives({ campaignId, force: true }));
                dispatch(markCampaignDataExpired({campaignId}));
                navigate('/campaign/' + campaignId + '/milestone');
            } catch (error) {
                toast.error(getErrorMessage(error, 'Failed to update influencer criteria!'));
            } finally {
                setLoading(false)
            }
        }
    });

    if (!campaignId) return <Error404 />

    if (!requirementLoading && !campaignRequirement) return <Error404 />

    return (
        <div className="page-content">
            <div className="page-content-inner page-spacing">
                <div className="d-flex w-100 justify-content-between align-items-center pt-1 pb-5">
                    <Stepper steps={steps} />
                </div>
                {
                    requirementLoading && <Loader />
                }
                {
                    !requirementLoading && campaignRequirement &&
                    <Form className='needs-validation' onSubmit={(e) => {
                        e.preventDefault();
                        formFields.handleSubmit();
                        return false;
                    }}>
                        <Card>
                            <CardBody className='py-0'>
                                <InfluencerCrieteria formFields={formFields} />
                            </CardBody>
                        </Card>
                        <div className="d-flex justify-content-between mb-4">
                            <Link to={{ pathname: `/campaign/${campaignId}/edit` }} type='button' className="theme-btn theme-btn-lg btn-primary-outlined"><i className='bx fs-4 bx-left-arrow-alt me-2' ></i>Back</Link>

                            <button type='submit' disabled={loading || !privileges.includes(PRIVILEGE.CAMPAIGN.CAMPAIGN.EDIT)} className="theme-btn theme-btn-lg btn-gradient form-continue-btn">
                                <React.Fragment>
                                    Proceed to Next
                                    {
                                        loading ? <Spinner size="sm" className='me-2'> Loading... </Spinner> : <i className='bx fs-4 bx-right-arrow-alt' ></i>
                                    }
                                </React.Fragment>
                            </button>
                        </div>
                    </Form>
                }
            </div>
        </div>
    )
}

export default withAccessControl(CampaignRequirement, PRIVILEGE.CAMPAIGN.CAMPAIGN.EDIT)